import React, { useState, useEffect, useRef } from 'react';
import { X, Camera, Package, ChevronDown, Save, Store, Sparkles, Tag, FileText } from 'lucide-react';
import { useLang } from '../../core/lang.jsx';
import LangKeys from '../../core/locales/keys';
import itemService from '../../services/itemService';
import UnitPickerModal from './UnitPickerModal';
import GstPickerModal from './GstPickerModal';
import CategoryPickerModal, { getCategoryDetails } from './CategoryPickerModal';
import PhotoGuidelinesAndVideoCard from './PhotoGuidelinesAndVideoCard';
import DescriptionToolbarTextarea from './DescriptionToolbarTextarea';
import ImageCropperModal from './ImageCropperModal';
import { compressImage } from '../../core/imageUtils';

const AddProductModal = ({ isOpen, onClose, item = null, onSuccess }) => {
  const { t } = useLang();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [showGstDetails, setShowGstDetails] = useState(false);
  const [showUnitPicker, setShowUnitPicker] = useState(false);
  const [showGstPicker, setShowGstPicker] = useState(false);
  const [showCategoryPicker, setShowCategoryPicker] = useState(false);
  const [showCropper, setShowCropper] = useState(false);
  const [rawImageSrc, setRawImageSrc] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    item_type: 'product',
    unit: 'PCS',
    sale_price: '',
    discount_price: '',
    purchase_price: '',
    tax_included: true,
    is_public: true,
    category: 'General',
    description: '',
    hsn_sac_code: '',
    gst_rate: '',
    opening_stock: '',
    low_stock_alert: '',
    photo_url: null,
    image_file: null
  });

  useEffect(() => {
    if (item) {
      setFormData({
        ...item,
        name: item.name || '',
        unit: item.unit || 'PCS',
        sale_price: item.sale_price !== null && item.sale_price !== undefined ? String(item.sale_price) : '',
        discount_price: item.discount_price ? String(item.discount_price) : '',
        purchase_price: item.purchase_price !== null && item.purchase_price !== undefined ? String(item.purchase_price) : '',
        opening_stock: item.opening_stock !== null && item.opening_stock !== undefined ? String(item.opening_stock) : '',
        low_stock_alert: item.low_stock_alert !== null && item.low_stock_alert !== undefined ? String(item.low_stock_alert) : '',
        is_public: item.is_public !== 0 && item.is_public !== false && item.is_public !== '0',
        category: item.category || 'General',
        description: item.description || '',
        tax_included: item.tax_included !== 0 && item.tax_included !== false && item.tax_included !== '0',
        photo_url: item.photo_url || null,
        image_file: null
      });
      setShowGstDetails(!!(item.gst_rate || item.hsn_sac_code));
    } else {
      setFormData({
        name: '',
        item_type: 'product',
        unit: 'PCS',
        sale_price: '',
        discount_price: '',
        purchase_price: '',
        tax_included: true,
        is_public: true,
        category: 'General',
        description: '',
        hsn_sac_code: '',
        gst_rate: '',
        opening_stock: '',
        low_stock_alert: '',
        photo_url: null,
        image_file: null
      });
      setShowGstDetails(false);
    }
  }, [item, isOpen]);

  if (!isOpen) return null;

  const handlePhotoClick = () => fileInputRef.current.click();
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setRawImageSrc(event.target.result);
        setShowCropper(true);
      };
      reader.readAsDataURL(file);
      e.target.value = '';
    }
  };

  const handleCropComplete = (croppedFile, previewUrl) => {
    setFormData((prev) => ({
      ...prev,
      photo_url: previewUrl,
      image_file: croppedFile
    }));
  };

  const selectedCategoryInfo = getCategoryDetails(formData.category, false, t);

  const handleSave = async () => {
    if (!formData.name.trim()) {
      alert(t(LangKeys.please_enter_item_name));
      return;
    }
    setLoading(true);

    try {
      const response = item 
        ? await itemService.updateItem(item.id, formData) 
        : await itemService.createItem(formData);

      if (response === true) {
        onSuccess();
        onClose();
      } else {
        alert("Failed to save: " + (response?.message || "Unknown server error"));
      }
    } catch (err) {
      console.error("Save error:", err);
      alert("An error occurred while saving.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        {/* Modal Header */}
        <div style={styles.header}>
          <div style={styles.headerTitleRow}>
            <h2 style={styles.title}>{item ? t(LangKeys.edit_item) : t(LangKeys.add_product)}</h2>
            <button style={styles.closeBtn} onClick={onClose}><X size={20} /></button>
          </div>
        </div>

        {/* Modal Body */}
        <div style={styles.body}>
          <input type="file" ref={fileInputRef} style={{ display: 'none' }} accept="image/*" onChange={handleFileChange} />

          {/* ITEM NAME & PHOTO */}
          <div style={styles.sectionCard}>
            <div style={styles.row}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>{t(LangKeys.item_name)}</label>
                <input
                  style={styles.input}
                  placeholder={t(LangKeys.enter_item_name_here)}
                  value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                />
              </div>
              <div style={styles.photoBox} onClick={handlePhotoClick}>
                <div style={styles.photoPreview}>
                  {formData.photo_url ? (
                    <img src={formData.photo_url} alt="" style={styles.img} />
                  ) : (
                    <Camera size={24} color="#94A3B8" />
                  )}
                </div>
                <span style={styles.photoText}>{t(LangKeys.photo)}</span>
              </div>
            </div>
          </div>

          {/* UNIT SELECTOR */}
          <div style={styles.sectionCard} onClick={() => setShowUnitPicker(true)}>
            <label style={styles.label}>{t(LangKeys.primary_unit)}</label>
            <div style={styles.selectBox}>
              <span>{formData.unit}</span>
              <ChevronDown size={18} color="#94A3B8" />
            </div>
          </div>

          {/* PRICE SECTION */}
          <div style={styles.sectionCard}>
            <div style={styles.row}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>{t(LangKeys.sale_price)}</label>
                <div style={styles.priceInputRow}>
                  <span style={{ color: '#16A34A', fontWeight: 'bold' }}>₹</span>
                  <input
                    type="number"
                    style={styles.priceInput}
                    placeholder="0"
                    value={formData.sale_price}
                    onChange={e => setFormData({ ...formData, sale_price: e.target.value })}
                  />
                </div>
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>{t(LangKeys.purchase_price)}</label>
                <div style={styles.priceInputRow}>
                  <span style={{ color: '#DC2626', fontWeight: 'bold' }}>₹</span>
                  <input
                    type="number"
                    style={styles.priceInput}
                    placeholder="0"
                    value={formData.purchase_price}
                    onChange={e => setFormData({ ...formData, purchase_price: e.target.value })}
                  />
                </div>
              </div>
            </div>
            <div style={styles.taxToggle}>
              <span>{t(LangKeys.tax_included)}</span>
              <div
                style={{ ...styles.switch, backgroundColor: formData.tax_included ? '#3B82F6' : '#E2E8F0' }}
                onClick={() => setFormData({ ...formData, tax_included: !formData.tax_included })}
              >
                <div style={{ ...styles.switchDot, left: formData.tax_included ? '22px' : '4px' }} />
              </div>
            </div>
          </div>

          {/* STOCK SECTION */}
          <div style={styles.sectionCard}>
            <div style={styles.row}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>{t(LangKeys.opening_stock)}</label>
                <input
                  type="number"
                  style={styles.input}
                  placeholder="0"
                  value={formData.opening_stock}
                  onChange={e => setFormData({ ...formData, opening_stock: e.target.value })}
                />
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>{t(LangKeys.low_stock_alert)}</label>
                <input
                  type="number"
                  style={styles.input}
                  placeholder="2"
                  value={formData.low_stock_alert}
                  onChange={e => setFormData({ ...formData, low_stock_alert: e.target.value })}
                />
              </div>
            </div>
          </div>

          {/* HSN & GST DETAILS */}
          {!showGstDetails ? (
            <button style={styles.gstToggleBtn} onClick={() => setShowGstDetails(true)}>
              + {t(LangKeys.add_hsn_and_gst_details)}
            </button>
          ) : (
            <div style={styles.sectionCard}>
              <div style={styles.row}>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>{t(LangKeys.hsn)}</label>
                  <input
                    style={styles.input}
                    placeholder={t(LangKeys.enter_hsn)}
                    value={formData.hsn_sac_code}
                    onChange={e => setFormData({ ...formData, hsn_sac_code: e.target.value })}
                  />
                </div>
                <div style={styles.inputGroup} onClick={() => setShowGstPicker(true)}>
                  <label style={styles.label}>{t(LangKeys.gst_rate_percent)}</label>
                  <div style={styles.selectBox}>
                    <span>{formData.gst_rate || t(LangKeys.enter_gst_rate)}</span>
                    <ChevronDown size={18} color="#94A3B8" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ONLINE MARKETPLACE TOGGLE & CATEGORY (AT BOTTOM LIKE MOBILE APP) */}
          <div style={styles.marketplaceCard}>
            <div style={styles.marketplaceHeader}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Store size={18} color="#2563EB" />
                <span style={styles.marketplaceTitle}>
                  {t(LangKeys.item_marketplace_title) || 'Online Marketplace'}
                </span>
              </div>
              <div
                style={{
                  ...styles.switch,
                  backgroundColor: formData.is_public ? '#16A34A' : '#E2E8F0'
                }}
                onClick={() => setFormData({ ...formData, is_public: !formData.is_public })}
              >
                <div style={{ ...styles.switchDot, left: formData.is_public ? '22px' : '4px' }} />
              </div>
            </div>

            <p style={styles.marketplaceSub}>
              {formData.is_public
                ? (t(LangKeys.item_marketplace_sub_active) || 'Visible in your online store and local search')
                : (t(LangKeys.item_marketplace_sub_inactive) || 'Private item, only visible in your Khata/Billing')}
            </p>

            {formData.is_public && (
              <div style={styles.onlineFields}>
                {/* Photo Guidelines & Video Guide Card */}
                <PhotoGuidelinesAndVideoCard
                  hasPhoto={!!(formData.photo_url || formData.image_file)}
                  isService={false}
                />

                {/* Category Selector */}
                <div>
                  <label style={styles.label}>{t(LangKeys.item_category_lbl) || 'Category'}</label>
                  <div style={styles.categorySelectBtn} onClick={() => setShowCategoryPicker(true)}>
                    <div style={styles.categoryIconWrap}>{selectedCategoryInfo.icon}</div>
                    <div style={{ flex: 1 }}>
                      <div style={styles.categoryTitle}>{selectedCategoryInfo.title}</div>
                      <div style={styles.categorySub}>{selectedCategoryInfo.subtitle}</div>
                    </div>
                    <button type="button" style={styles.changeBadge}>
                      {t(LangKeys.change_btn) || 'Change'} <ChevronDown size={14} />
                    </button>
                  </div>
                </div>

                {/* Optional Discount Price */}
                <div style={{ marginTop: '12px' }}>
                  <label style={styles.label}>
                    {t(LangKeys.item_discount_price_lbl) || 'Discount / Offer Price (Optional)'}
                  </label>
                  <div style={styles.priceInputRow}>
                    <span style={{ color: '#2563EB', fontWeight: 'bold' }}>₹</span>
                    <input
                      type="number"
                      style={styles.priceInput}
                      placeholder={t(LangKeys.item_discount_price_hint) || 'e.g. ₹950 (Customers will see discount)'}
                      value={formData.discount_price}
                      onChange={e => setFormData({ ...formData, discount_price: e.target.value })}
                    />
                  </div>
                </div>

                {/* Description & Rich SEO Formatting Toolbar */}
                <div style={{ marginTop: '14px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                    <label style={styles.label}>
                      {t(LangKeys.item_desc_lbl) || 'Description & Details (For SEO)'}
                    </label>
                    <span style={styles.seoBadge}>
                      <Sparkles size={11} color="#2563EB" /> {t(LangKeys.market_seo_badge) || 'Google & Search SEO'}
                    </span>
                  </div>
                  <DescriptionToolbarTextarea
                    value={formData.description}
                    onChange={val => setFormData({ ...formData, description: val })}
                    itemName={formData.name}
                    isService={false}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Modal Footer */}
        <div style={styles.footer}>
          <button style={styles.saveBtn} onClick={handleSave} disabled={loading}>
            {loading ? (
              <div className="spinner-small" />
            ) : (
              <>
                <Package size={18} /> {item ? t(LangKeys.save_changes) : t(LangKeys.add_product)}
              </>
            )}
          </button>
        </div>
      </div>

      {/* Pickers */}
      <UnitPickerModal
        isOpen={showUnitPicker}
        onClose={() => setShowUnitPicker(false)}
        onSelect={(u) => setFormData({ ...formData, unit: u })}
      />
      <GstPickerModal
        isOpen={showGstPicker}
        onClose={() => setShowGstPicker(false)}
        onSelect={(g) => setFormData({ ...formData, gst_rate: g })}
        selectedValue={formData.gst_rate}
      />
      <CategoryPickerModal
        isOpen={showCategoryPicker}
        onClose={() => setShowCategoryPicker(false)}
        selectedId={formData.category}
        onSelect={(catId) => setFormData({ ...formData, category: catId })}
        isService={false}
      />
      <ImageCropperModal
        isOpen={showCropper}
        imageSrc={rawImageSrc}
        onClose={() => setShowCropper(false)}
        onCropComplete={handleCropComplete}
        aspectRatio={1}
        title={t(LangKeys.crop_product_photo)}
      />

      <style>{`
        .spinner-small {
          width: 20px;
          height: 20px;
          border: 2px solid #FFF;
          border-top: 2px solid transparent;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000
  },
  modal: {
    width: '100%',
    maxWidth: '520px',
    maxHeight: '95vh',
    backgroundColor: '#FFF',
    borderRadius: '24px',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)'
  },
  header: {
    padding: '22px 24px',
    color: '#FFF',
    backgroundColor: '#3B82F6'
  },
  headerTitleRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  title: {
    fontSize: '20px',
    fontWeight: '800',
    margin: 0
  },
  closeBtn: {
    border: 'none',
    backgroundColor: 'rgba(255,255,255,0.2)',
    color: '#FFF',
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer'
  },
  body: {
    padding: '20px',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    backgroundColor: '#F8FAFC'
  },
  sectionCard: {
    backgroundColor: '#FFF',
    padding: '16px',
    borderRadius: '16px',
    border: '1px solid #E2E8F0',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  marketplaceCard: {
    backgroundColor: '#FFF',
    padding: '16px',
    borderRadius: '16px',
    border: '1px solid #DBEAFE',
    backgroundColor: '#F0F7FF',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  marketplaceHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  marketplaceTitle: {
    fontSize: '14px',
    fontWeight: '800',
    color: '#1E3A8A'
  },
  marketplaceSub: {
    fontSize: '12px',
    color: '#64748B',
    margin: 0,
    lineHeight: '1.4'
  },
  onlineFields: {
    marginTop: '6px',
    paddingTop: '12px',
    borderTop: '1px solid #DBEAFE',
    display: 'flex',
    flexDirection: 'column'
  },
  categorySelectBtn: {
    marginTop: '6px',
    padding: '10px 12px',
    backgroundColor: '#FFF',
    borderRadius: '12px',
    border: '1px solid #CBD5E1',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    cursor: 'pointer'
  },
  categoryIconWrap: {
    fontSize: '22px',
    width: '36px',
    height: '36px',
    borderRadius: '8px',
    backgroundColor: '#F8FAFC',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid #E2E8F0'
  },
  categoryTitle: {
    fontSize: '13px',
    fontWeight: '700',
    color: '#0F172A'
  },
  categorySub: {
    fontSize: '11px',
    color: '#64748B'
  },
  changeBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '5px 10px',
    borderRadius: '8px',
    backgroundColor: '#EFF6FF',
    border: '1px solid #BFDBFE',
    color: '#2563EB',
    fontSize: '11px',
    fontWeight: '700',
    cursor: 'pointer'
  },
  seoBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '3px 8px',
    backgroundColor: '#EFF6FF',
    borderRadius: '6px',
    border: '1px solid #DBEAFE',
    color: '#2563EB',
    fontSize: '10px',
    fontWeight: '700'
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '10px',
    border: '1px solid #CBD5E1',
    backgroundColor: '#FFF',
    fontSize: '13px',
    color: '#1E293B',
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    resize: 'vertical'
  },
  row: {
    display: 'flex',
    gap: '16px'
  },
  inputGroup: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
  },
  label: {
    fontSize: '12px',
    fontWeight: '700',
    color: '#64748B'
  },
  input: {
    height: '40px',
    border: 'none',
    borderBottom: '2px solid #F1F5F9',
    outline: 'none',
    fontSize: '14px',
    fontWeight: '600',
    color: '#1E293B',
    backgroundColor: 'transparent'
  },
  photoBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
    cursor: 'pointer'
  },
  photoPreview: {
    width: '60px',
    height: '60px',
    borderRadius: '12px',
    backgroundColor: '#F1F5F9',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid #E2E8F0',
    overflow: 'hidden'
  },
  photoText: {
    fontSize: '11px',
    color: '#94A3B8',
    fontWeight: '600'
  },
  img: {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  },
  selectBox: {
    height: '40px',
    borderBottom: '2px solid #F1F5F9',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  priceInputRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    borderBottom: '2px solid #F1F5F9',
    height: '40px'
  },
  priceInput: {
    flex: 1,
    height: '40px',
    border: 'none',
    outline: 'none',
    fontSize: '14px',
    fontWeight: '700',
    backgroundColor: 'transparent'
  },
  taxToggle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '6px',
    fontSize: '13px',
    fontWeight: '600',
    color: '#1E293B'
  },
  switch: {
    width: '44px',
    height: '24px',
    borderRadius: '20px',
    position: 'relative',
    cursor: 'pointer',
    transition: '0.3s'
  },
  switchDot: {
    width: '18px',
    height: '18px',
    backgroundColor: '#FFF',
    borderRadius: '50%',
    position: 'absolute',
    top: '3px',
    transition: '0.3s',
    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
  },
  gstToggleBtn: {
    border: 'none',
    background: 'none',
    color: '#3B82F6',
    fontWeight: '800',
    fontSize: '13px',
    cursor: 'pointer',
    padding: '8px',
    textAlign: 'center'
  },
  footer: {
    padding: '16px 20px',
    borderTop: '1px solid #F1F5F9',
    backgroundColor: '#FFF'
  },
  saveBtn: {
    width: '100%',
    height: '50px',
    backgroundColor: '#3B82F6',
    border: 'none',
    color: '#FFF',
    borderRadius: '14px',
    fontWeight: '800',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    cursor: 'pointer',
    boxShadow: '0 4px 15px rgba(59, 130, 246, 0.35)',
    fontSize: '15px'
  }
};

export default AddProductModal;
